import { useMemo } from "react";
import { MultiOptionInput } from "../../Inputs/MultiOptionInput";
import { useTranslations } from "next-intl";
import { OptionChip } from "@/lib/utils/optionChipColor";
import { isDanglingDynamicId, isUnresolvedWhilePending } from "./danglingDynamicId";
import { defaultChartColors } from "@/pages/event/dashboard/components/Tables/sliceColors";

type TableCellContentMultiSelectProps = {
  editable: boolean;
  isFilter?: boolean;
  border?: string;
  additionalField: boolean;
  options: string[];
  optionColors?: Record<string, string>;
  /** Dynamic id→name display map for optionSource fields (offering/room/etc.).
   *  When present, options display by name while the stored value stays the id. */
  dynamicLabels?: Record<string, string>;
  /**
   * Per-field i18n namespace for the option keys, instead of the shared
   * `optionsMultiSingleSelect` catalogue. See `Field.optionNamespace` — the
   * automation-history `source` / `trigger` columns own their labels in
   * `automation` / `mailTemplateCreation`, and the shared catalogue both lacks
   * most of those keys and already collides on `CHECKIN_APP`.
   */
  optionNamespace?: string;
  /** The optionSource fetch is still in flight, so an id missing from the
   *  catalog means "not loaded yet", not "referenced thing was deleted". */
  optionsPending?: boolean;
  required: boolean;
  value: string[];
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string[]) => void;
  onFocusAction?: () => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
  /** Set when a SINGLE_SELECT field is rendered here in filter mode (multi-pick).
   *  Drives the built-in raw-value (timeZone) / always-set (guestType) handling
   *  that TCCSingleSelect applies, so built-in selects don't show raw codes or a
   *  meaningless "—" option. */
  fieldName?: string;
  /** MULTI_SELECT max-selection count (field.upperBound). Blocks picking more
   *  than N options during value entry. Never applied in filter mode. */
  maxSelections?: number;
  /** Raw option values that are full (per-option capacity) — greyed out in the
   *  picker unless already held. Never applied in filter mode. */
  disabledOptions?: string[];
};

export const TableCellContentMultiSelect: React.FC<
  TableCellContentMultiSelectProps
> = ({
  editable,
  isFilter,
  border,
  additionalField,
  options,
  optionColors,
  dynamicLabels,
  optionNamespace,
  optionsPending,
  required,
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  error,
  height,
  surface,
  fontSize,
  padding,
  rounded,
  viewOnly,
  fieldName,
  maxSelections,
  disabledOptions,
}) => {
  const t = useTranslations("optionsMultiSingleSelect");
  // Root translator, so a per-field namespace can be resolved by dotted path.
  // `useTranslations(<runtime string>)` is not an option — it is a hook, and
  // the namespace varies per field.
  const tRoot = useTranslations();

  // Built-in SINGLE_SELECT fields routed here in filter mode (multi-pick) need
  // the same special-casing TCCSingleSelect applies: timeZone stores/shows raw
  // values (no translation), guestType is always set (mainGuest | companion) so
  // a "—" not-set pick is meaningless. language needs nothing — its t(option)
  // labels already flow through the non-additionalField branch below.
  const isTimeZoneField = fieldName === "timeZone";
  const isAlwaysSetField = fieldName === "guestType";

  // Filter rows expose a "—" (not-set) pick that COEXISTS with real options: it
  // maps to the empty-string sentinel "" in the value array (e.g. ["M",""]),
  // which splits into a "∅" (is empty) OR-row at serialize time (see
  // coerceEmptyEqualsOperator / flattenArrayFilterValues) so the filter matches
  // null/unset rows alongside the picked values. An empty array [] means nothing
  // picked (no filter). Edit rows don't get it — clearing a multi-select to unset
  // isn't a supported write.
  const showEmptyOption = !!isFilter && !isAlwaysSetField;
  const EMPTY_DISPLAY = "—";

  // Create a proper Map with original options as keys and translated/original values as values
  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) => {
      // ⚠ A FUNCTION, not a const. Hoisting this into a `const translated =`
      // made it eager, so every option was looked up even when `dynamicLabels`
      // or the raw-value branch below already answered — and each miss builds
      // an Error with a stack inside next-intl. A `timeZone` filter cell threw
      // and swallowed 61 of them per render, with identical output, which is
      // why the whole suite stayed green. `FilterGrid` got this right.
      // `tRoot.has` first — see FilterGrid: a root-translator miss falls back to
      // the DOTTED key, which is worse output than the bare value the shared
      // namespace produced before the pointer existed.
      const translate = () => {
        if (optionNamespace === undefined) return t(option);
        const key = `${optionNamespace}.${option}`;
        return tRoot.has(key) ? tRoot(key) : option;
      };
      const displayValue =
        dynamicLabels?.[option] ??
        (additionalField || isTimeZoneField ? option : translate());
      map.set(option, displayValue);
    });
    return map;
  }, [options, additionalField, isTimeZoneField, t, tRoot, optionNamespace, dynamicLabels]);

  // Convert the Map values to an array for the MultiOptionInput
  const displayOptions = useMemo(() => {
    const base = Array.from(optionsMap.values());
    // Don't double-add if a (custom) option's display already is "—".
    return showEmptyOption && !base.includes(EMPTY_DISPLAY)
      ? [...base, EMPTY_DISPLAY]
      : base;
  }, [optionsMap, showEmptyOption]);

  // Convert original values to display values for defaultValues. In filter mode
  // the empty-string sentinel "" IS the "—" (is-empty) pick — render it as
  // EMPTY_DISPLAY so its checkbox shows checked and can COEXIST with real option
  // chips (e.g. "M" or unset"). An empty value array now means "nothing picked"
  // → nothing checked (no filter for this field), distinct from an explicit "—".
  // (Edit mode has no "—" option, so empty just renders no chips.)
  const displayValues = useMemo(() => {
    return (value || []).map((val) =>
      showEmptyOption && val === "" ? EMPTY_DISPLAY : optionsMap.get(val) || val,
    );
  }, [optionsMap, value, showEmptyOption]);

  // Function to get the original key from a display value
  const getKeyFromDisplayValue = (displayValue: string): string => {
    for (const [key, val] of optionsMap.entries()) {
      if (val === displayValue) {
        return key;
      }
    }
    return displayValue; // Fallback to the display value if no key is found
  };

  // Convert the display values back to their original keys. "—" (is-empty) maps
  // to the empty-string sentinel "" and MAY coexist with real options — the ""
  // element is split off into its own "∅" (is-empty) OR-row at serialize time
  // (parser.ts), so "gender = M OR gender unset" is expressible in one dropdown.
  // An empty selection emits [] → no filter for this field.
  const handleSetParent = (selectedDisplayValues: string[]) => {
    const originalKeys = selectedDisplayValues.map((displayValue) =>
      showEmptyOption && displayValue === EMPTY_DISPLAY
        ? ""
        : getKeyFromDisplayValue(displayValue),
    );

    onChangeValue(originalKeys);
  };

  // Effective per-option colors: custom (additionalField) SELECTs fall back to
  // the same default chart palette the editor swatches and dashboard charts use,
  // so every real option shows a colored chip. Explicit hex colors win.
  const effectiveColors = useMemo(
    () =>
      additionalField
        ? { ...defaultChartColors(options), ...(optionColors ?? {}) }
        : optionColors ?? {},
    [additionalField, options, optionColors],
  );

  // One chip per selected value — color is per-option, never per-cell. Stale
  // values not in `options` have no color and render as the neutral chip.
  //
  // PURE DISPLAY, unlike `displayValues` above: chips never feed
  // getKeyFromDisplayValue, so masking here cannot be written back. Masking in
  // `displayValues` would be a real data bug — MultiOptionInput holds display
  // strings as state and re-emits the whole array on any toggle, so a
  // placeholder would be saved as the value, and two dangling ids sharing one
  // placeholder would collapse into a single entry.
  const valueChips = useMemo(
    () =>
      (value || []).map((val) => {
        let label: string;
        if (isUnresolvedWhilePending(val, optionsMap, dynamicLabels, optionsPending)) {
          label = "";
        } else if (isDanglingDynamicId(val, optionsMap, dynamicLabels)) {
          label = t("deletedReferenceLabel", { idTail: val.slice(-4) });
        } else {
          label = optionsMap.get(val) || val;
        }
        return { key: val, label, color: effectiveColors[val] };
      }),
    [value, optionsMap, effectiveColors, dynamicLabels, optionsPending, t],
  );

  return (
    <>
      {isEditing && isSelected && (editable || isFilter) ? (
        <div className="flex flex-col flex-1 min-w-0">
          <MultiOptionInput
            disabled={viewOnly}
            height={height}
            surface={surface}
            fontSize={fontSize}
            padding={padding}
            rounded={rounded}
            border={border}
            setParent={handleSetParent}
            options={displayOptions}
            defaultValues={displayValues}
            // "—" (is-empty) coexists with real options; the "" sentinel it maps
            // to is OR-combined with the picked values at serialize time.
            // Selection-count cap only applies to value entry, never filtering.
            maxSelections={isFilter ? undefined : maxSelections}
            // Filter cells stay blank until a value is picked — the other filter
            // inputs (text/date) show no placeholder either.
            placeholder={isFilter ? "" : undefined}
            // Full options greyed out (mapped raw→display keys). Not in filters.
            disabledOptions={
              isFilter
                ? undefined
                : disabledOptions?.map((k) => optionsMap.get(k) ?? k)
            }
            required={required}
            onFocusAction={onFocusAction}
            optionColors={effectiveColors}
            // Alphabetize user-defined option lists and the raw-value/language
            // built-ins (sort by shown name); built-in semantic enums
            // (gender/state/guestType) keep their deliberate order.
            sortOptions={additionalField || isTimeZoneField || fieldName === "language"}
          />
          {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
        </div>
      ) : valueChips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          {valueChips.map((chip) => (
            <OptionChip key={chip.key} label={chip.label} color={chip.color} />
          ))}
        </div>
      ) : (
        <p />
      )}
    </>
  );
};
